import { useContext, useEffect, useState, type ReactNode } from "react";
import { DashboardContext } from "./DashboardContext";
import { BRAND_SECONDARY_RGB } from "~/brandColors";

type ChartTitleProps = {
  text: string;
  classNames?: string;
  variant?: "window" | "plain";
  draggable?: boolean;
  forceWindow?: boolean;
  /** Optional element rendered next to the centered title text (e.g. an edit pen). */
  leftAdornment?: ReactNode;
  /** Optional content on the same row as the title (e.g. portfolio metrics strip). */
  endContent?: ReactNode;
};

function ChartTitle({
  text,
  classNames,
  variant = "window",
  draggable = true,
  forceWindow = false,
  leftAdornment,
  endContent,
}: ChartTitleProps) {
  const { isDashboard } = useContext(DashboardContext);
  const [isEditing, setIsEditing] = useState(false);

  // Sync with global editing state via event and optional URL param
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("edit") === "true") {
        setIsEditing(true);
      }
    } catch {}

    const handler = (e: Event) => {
      const ce = e as CustomEvent<boolean>;
      setIsEditing(Boolean(ce.detail));
    };
    window.addEventListener(
      "dashboard:editing-change",
      handler as EventListener
    );
    return () =>
      window.removeEventListener(
        "dashboard:editing-change",
        handler as EventListener
      );
  }, []);

  const effectiveVariant = isDashboard || forceWindow ? variant : "plain";
  if (effectiveVariant === "plain") {
    return (
      <div
        className={`text-center text-[30px] font-[500] text-menu mobile:text-[25px] ${
          classNames ? classNames : ""
        }`}
      >
        {text}
      </div>
    );
  }

  if (endContent) {
    return (
      <div
        className={`${
          isDashboard && draggable && isEditing
            ? "rgl-drag-handle cursor-move select-none"
            : ""
        } relative flex min-h-0 w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-3 py-1.5 text-menu sm:min-h-10 sm:py-2 ${
          classNames ? classNames : ""
        }`}
      >
        <div
          className="shrink-0 truncate text-[20px] font-[600] leading-tight sm:text-[22px] md:text-[28px]"
          title={text}
        >
          {text}
        </div>
        <div className="min-w-0 max-w-full shrink">{endContent}</div>
        {leftAdornment && (
          <div className="absolute start-2 flex items-center">{leftAdornment}</div>
        )}
        {isDashboard && draggable && isEditing && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            width="16"
            height="16"
            className="pointer-events-none absolute right-2 opacity-60"
            fill={BRAND_SECONDARY_RGB}
          >
            <path d="M227.408 90.699l-39.646 13.868c-20.177 7.793-36.668-5.311-22.629-19.536L240.632 9.53c12.708-12.706 18.028-12.706 30.736 0l75.5 75.501c14.038 14.225-2.454 27.329-22.629 19.536l-39.646-13.868v136.708h136.708l-13.869-39.647c-7.792-20.175 5.312-36.667 19.536-22.628l75.502 75.499c12.706 12.708 12.706 18.028 0 30.736l-75.502 75.5c-14.224 14.038-27.328-2.453-19.536-22.629l13.869-39.646H284.593v136.709l39.646-13.869c20.175-7.792 36.667 5.312 22.629 19.537l-75.5 75.501c-12.708 12.706-18.028 12.706-30.736 0l-75.499-75.501c-14.039-14.225 2.452-27.329 22.629-19.537l39.646 13.869V284.592H90.699l13.868 39.646c7.793 20.176-5.311 36.667-19.536 22.629l-75.501-75.5c-12.706-12.708-12.706-18.028 0-30.736l75.501-75.499c14.225-14.039 27.329 2.453 19.536 22.628l-13.868 39.647h136.709V90.699z" />
          </svg>
        )}
      </div>
    );
  }

  return (
    <div
      className={`${
        isDashboard && draggable && isEditing
          ? "rgl-drag-handle cursor-move select-none"
          : ""
      } relative flex h-10 w-full items-center justify-center gap-2 rounded-t-md border-b border-secondary bg-background/40 px-3 text-menu shadow-sm ${
        classNames ? classNames : ""
      }`}
    >
      <div
        className="truncate text-[28px] font-[600] leading-tight mobile:text-[22px]"
        title={text}
      >
        {text}
      </div>
      {leftAdornment && (
        <div className="absolute start-2 flex items-center">
          {leftAdornment}
        </div>
      )}
      {isDashboard && draggable && isEditing && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          width="16"
          height="16"
          className="pointer-events-none absolute right-2 opacity-60"
          fill={BRAND_SECONDARY_RGB}
        >
          <path d="M227.408 90.699l-39.646 13.868c-20.177 7.793-36.668-5.311-22.629-19.536L240.632 9.53c12.708-12.706 18.028-12.706 30.736 0l75.5 75.501c14.038 14.225-2.454 27.329-22.629 19.536l-39.646-13.868v136.708h136.708l-13.869-39.647c-7.792-20.175 5.312-36.667 19.536-22.628l75.502 75.499c12.706 12.708 12.706 18.028 0 30.736l-75.502 75.5c-14.224 14.038-27.328-2.453-19.536-22.629l13.869-39.646H284.593v136.709l39.646-13.869c20.175-7.792 36.667 5.312 22.629 19.537l-75.5 75.501c-12.708 12.706-18.028 12.706-30.736 0l-75.499-75.501c-14.039-14.225 2.452-27.329 22.629-19.537l39.646 13.869V284.592H90.699l13.868 39.646c7.793 20.176-5.311 36.667-19.536 22.629l-75.501-75.5c-12.706-12.708-12.706-18.028 0-30.736l75.501-75.499c14.225-14.039 27.329 2.453 19.536 22.628l-13.868 39.647h136.709V90.699z" />
        </svg>
      )}
    </div>
  );
}
export default ChartTitle;
